import React, { useContext } from 'react';
import cx from 'classnames';
import { uid } from 'uid';
import useThemeBlock from '@/hooks/useThemeBlock';
import { LocaleContext } from '@/contexts/locale-context';
import formBlock from '@/components/molecules/Form/block.json';
import block from './block.json';
// styles
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type InputTextareaProps = InputProps & ThemeProps;

interface ForwardedInputTextarea
	extends React.ForwardRefExoticComponent<
			InputTextareaProps & React.RefAttributes<HTMLTextAreaElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const InputTextarea = React.forwardRef<
	HTMLTextAreaElement,
	InputTextareaProps
>(
	(
		{
			id = uid(),
			label,
			name,
			placeholder,
			fullSize = true,
			required = false,
			disabled = false,
			invalid,
			inputAttributes,
			theme = {},
		},
		ref
	) => {
		const { blockTheme: formBlockTheme } = useThemeBlock(
			formBlock.slug,
			theme
		);

		const rootClass = cx(
			'supt-inputTextarea supt-inputWrapper supt-inputField',
			fullSize ? 'supt-inputField--full' : 'supt-inputField--half',
			invalid && typeof invalid === 'string' && 'supt-inputField--error',
			getStyles(formBlockTheme)
		);
		const { __t } = useContext(LocaleContext);

		return (
			<div className={rootClass}>
				<label
					htmlFor={id}
					className="supt-inputTextarea__label supt-inputField__label"
					data-optional={
						required ? '' : __t('form-input-optional', 'optional')
					}
				>
					{label}
				</label>
				<textarea
					id={id}
					className="supt-inputTextarea__input supt-inputField__input"
					name={name}
					placeholder={placeholder}
					required={required}
					disabled={disabled}
					rows={4}
					ref={ref}
					aria-invalid={!!invalid}
					{...inputAttributes}
				/>
				{invalid && typeof invalid === 'string' ? (
					<span
						role="alert"
						className="supt-inputTextarea__error supt-inputField__error"
					>
						{invalid}
					</span>
				) : null}
			</div>
		);
	}
) as ForwardedInputTextarea;

InputTextarea.slug = block.slug;
InputTextarea.title = block.title;
